/**
 * Persistencia del módulo radio.
 *
 * Lectura/escritura en `localStorage` con validación defensiva: si los datos
 * guardados están corruptos o son de una versión incompatible, se descartan
 * silenciosamente y se vuelve a defaults.
 *
 * Limitación conocida: las blob URLs de tracks de usuario NO se persisten
 * entre sesiones (es una limitación del navegador). Lo que sí persiste es
 * el `id` y el `name` de cada track (en `userTrackMeta`), de manera que
 * cuando el usuario re-elija la carpeta, `reconcileGhosts()` (definido en
 * UI más adelante) puede re-asociar las playlists guardadas con los
 * archivos reales por nombre.
 */

import { DEFAULT_HOTSPOTS, DEFAULT_PLAYLIST, HOTSPOTS_KEY, STORAGE_KEY } from './data.js';
import { state } from './state.js';
import type { HotspotId, HotspotMap, HotspotRect, Playlist } from './types.js';
import { isPlainObject } from '@51esc/shared';

// ─── Playlists ───────────────────────────────────────────────────────────

/**
 * Carga las playlists guardadas. Garantiza que la default playlist siempre
 * existe en la posición [0], aunque el usuario haya borrado el localStorage.
 *
 * @returns Lista de playlists. Como mínimo contiene la default.
 */
export function loadPlaylists(): Playlist[] {
  let saved: unknown = null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [structuredClone(DEFAULT_PLAYLIST)];
    saved = JSON.parse(raw);
  } catch (e) {
    console.warn('[radio] load playlists failed:', e);
    return [structuredClone(DEFAULT_PLAYLIST)];
  }

  if (!Array.isArray(saved)) {
    return [structuredClone(DEFAULT_PLAYLIST)];
  }

  const out: Playlist[] = [];

  // La default playlist ("51 ESCUADRÓN PLAYLIST") es server-backed: su
  // contenido lo fija el manifiesto `/music/index.json` en cada arranque
  // (ver server-music.ts), NO localStorage. Por eso se reconstruye siempre
  // limpia desde DEFAULT_PLAYLIST y se ignora lo que hubiera guardado.
  out.push(structuredClone(DEFAULT_PLAYLIST));

  // Resto de playlists del usuario, validando shape mínimo
  for (const p of saved) {
    if (!isPlainObject(p)) continue;
    if (p.id === DEFAULT_PLAYLIST.id) continue;
    if (typeof p.id !== 'string' || typeof p.name !== 'string') continue;
    if (typeof p.coverId !== 'string') continue;
    if (!Array.isArray(p.tracks)) continue;
    out.push({
      id: p.id,
      name: p.name,
      coverId: p.coverId,
      tracks: p.tracks as string[],
      locked: p.locked === true,
      userTrackMeta: Array.isArray(p.userTrackMeta)
        ? (p.userTrackMeta as { id: string; name: string }[])
        : undefined,
    });
  }
  return out;
}

/**
 * Guarda las playlists actuales en localStorage. Para cada playlist,
 * incluye `userTrackMeta` con el id+name de cada track de usuario que
 * referencia, así sobreviven a recargas como ghosts identificables.
 */
export function savePlaylists(): void {
  try {
    const ser = state.playlists.map((p) => {
      const userTrackMeta = (p.tracks ?? [])
        .map((tid) => state.userTracks.find((u) => u.id === tid))
        .filter((t): t is NonNullable<typeof t> => Boolean(t))
        .map((t) => ({ id: t.id, name: t.name }));
      return {
        id: p.id,
        name: p.name,
        coverId: p.coverId,
        tracks: p.tracks,
        locked: p.locked,
        userTrackMeta: userTrackMeta.length ? userTrackMeta : undefined,
      };
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ser));
  } catch (e) {
    console.warn('[radio] save playlists failed:', e);
  }
}

// ─── Hotspots ────────────────────────────────────────────────────────────

/**
 * Comprueba que un objeto cualquiera tiene la forma de `HotspotRect`.
 * Defensivo: protege de basura en localStorage causada por versiones
 * antiguas o ediciones manuales.
 */
function isValidRect(v: unknown): v is HotspotRect {
  return (
    isPlainObject(v) &&
    typeof v.left === 'number' &&
    typeof v.top === 'number' &&
    typeof v.width === 'number' &&
    typeof v.height === 'number'
  );
}

/**
 * Carga las posiciones de los hotspots. Mergea las guardadas con los
 * defaults, así si hay un hotspot nuevo en el código que no existía cuando
 * el usuario guardó (por ejemplo añadimos un botón en el futuro), se usa
 * el default para ese y se conservan los del usuario para los demás.
 */
export function loadHotspots(): HotspotMap {
  const out = structuredClone(DEFAULT_HOTSPOTS);
  let saved: unknown = null;
  try {
    const raw = localStorage.getItem(HOTSPOTS_KEY);
    if (!raw) return out;
    saved = JSON.parse(raw);
  } catch (e) {
    console.warn('[radio] load hotspots failed:', e);
    return out;
  }
  if (!isPlainObject(saved)) return out;
  // Override sólo claves conocidas y rectángulos válidos
  for (const key of Object.keys(out) as HotspotId[]) {
    const v = saved[key];
    if (isValidRect(v)) out[key] = v;
  }
  return out;
}

/** Guarda las posiciones actuales de los hotspots. */
export function saveHotspots(): void {
  try {
    localStorage.setItem(HOTSPOTS_KEY, JSON.stringify(state.hotspots));
  } catch (e) {
    console.warn('[radio] save hotspots failed:', e);
  }
}
